import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MetricCard from '../MetricCard';
import ModernLoader from '../ModernLoader';
import MonetizedPagesList from '../monetized/MonetizedPagesList';
import MonetizationCharts from '../monetized/MonetizationCharts';
import PageDetailsModal from '../monetized/PageDetailsModal';
import styles from '../../styles/Team.module.css';

const MonetizedPage = ({ selectedPage, timeRange }) => {
  const [monetizedData, setMonetizedData] = useState(null);
  const [selectedPageData, setSelectedPageData] = useState(null);
  const [isPageDetailsOpen, setIsPageDetailsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchMonetizedData = useCallback(async () => {
    setLoading(true);
    try {
      // Step 1: Calculate date range based on timeRange (same as modal)
      const endDate = new Date();
      const startDate = new Date();
      
      switch (timeRange) {
        case 1: // Yesterday - only 1 day (yesterday to yesterday)
          startDate.setDate(endDate.getDate());
          endDate.setDate(endDate.getDate());
          break;
        case 7: // Last 7 days (today - 6 to today)
          startDate.setDate(endDate.getDate() - 6);
          // endDate stays as today
          break;
        case 30: // Last 30 days (today - 29 to today)
          startDate.setDate(endDate.getDate() - 29);
          // endDate stays as today
          break;
        case 90: // Last 90 days (today - 89 to today)
          startDate.setDate(endDate.getDate() - 89);
          // endDate stays as today
          break;
        default:
          startDate.setDate(endDate.getDate() - 29);
          // endDate stays as today
      }

      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      console.log('Date range:', { timeRange, startDateStr, endDateStr, startDate, endDate });

      // Step 1: Get summary data using /daily WITHOUT page_id (gets all pages)
      const summaryResponse = await fetch(
        `https://u6rn7wtdel.execute-api.us-east-1.amazonaws.com/Prod/daily?start_date=${startDateStr}&end_date=${endDateStr}`
      );
      const summaryData = await summaryResponse.json();
      
      if (summaryData.success) {
        // Extract unique page IDs and pre-filter by earnings > 0
        const pageSummaries = {};
        
        summaryData.data.forEach(day => {
          const pageId = day.page_id;
          if (!pageSummaries[pageId]) {
            pageSummaries[pageId] = {
              page_id: pageId,
              page_name: day.page_name,
              summaryEarnings: 0
            };
          }
          pageSummaries[pageId].summaryEarnings += parseFloat(day.approximate_monetization_earnings) || 0;
        });
        
        // Only process pages with earnings > 0
        const pagesWithEarnings = Object.values(pageSummaries)
          .filter(page => page.summaryEarnings > 0);
        
        console.log(`Processing ${pagesWithEarnings.length} pages with earnings > 0`);
        
        // Step 2: Get full date range data ONLY for pages with earnings > 0
        const pageAggregates = {};
        
        for (const pageSummary of pagesWithEarnings) {
          const pageId = pageSummary.page_id;
          
          const pageResponse = await fetch(
            `https://u6rn7wtdel.execute-api.us-east-1.amazonaws.com/Prod/daily?page_id=${pageId}&start_date=${startDateStr}&end_date=${endDateStr}`
          );
          const pageData = await pageResponse.json();
          
          if (pageData.success) {
            // Aggregate daily data for this page (EXACTLY like modal does)
            let totalEarnings = 0;
            let totalVideoViews = 0;
            let totalImpressions = 0;
            let totalPageViews = 0;
            let pageName = '';
            
            pageData.data.forEach(day => {
              if (day.page_id === pageId) {
                totalEarnings += parseFloat(day.approximate_monetization_earnings) || 0;
                totalVideoViews += parseInt(day.page_video_views) || 0;
                totalImpressions += parseInt(day.page_impressions) || 0;
                totalPageViews += parseInt(day.page_views_total) || 0;
                pageName = day.page_name;
              }
            });
            
            pageAggregates[pageId] = {
              page_id: pageId,
              page_name: pageName,
              total_approximate_earnings: totalEarnings,
              total_video_views: totalVideoViews,
              total_impressions: totalImpressions,
              total_page_views: totalPageViews,
              // Calculate RPM using EXACTLY the same method as modal
              video_rpm: totalVideoViews > 0 ? (totalEarnings / totalVideoViews) * 1000 : 0
            };
          }
        }
        
        // Convert to array and sort by earnings descending
        const monetizedPages = Object.values(pageAggregates)
          .sort((a, b) => b.total_approximate_earnings - a.total_approximate_earnings);
        
        setMonetizedData(monetizedPages);
      } else {
        console.error('MonetizedPage - Summary API failed:', summaryData);
      }
    } catch (error) {
      console.error('MonetizedPage - Error fetching monetized data:', error);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  // Load data on component mount and when timeRange changes
  useEffect(() => {
    fetchMonetizedData();
  }, [fetchMonetizedData]);

  const openPageDetails = (pageData) => {
    setSelectedPageData(pageData);
    setIsPageDetailsOpen(true);
  };

  const closePageDetails = () => {
    setIsPageDetailsOpen(false);
    setSelectedPageData(null);
  };

  if (loading) {
    return <ModernLoader message="Processing monetization data" size="default" />;
  }

  if (!monetizedData) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.errorMessage}>Failed to load monetization data</div>
      </div>
    );
  }

  // Calculate summary metrics from the aggregated daily data (same as modal)
  const totalEarnings = monetizedData.reduce((sum, page) => 
    sum + parseFloat(page.total_approximate_earnings), 0
  );
  
  const totalVideoViews = monetizedData.reduce((sum, page) => 
    sum + parseInt(page.total_video_views), 0
  );
  
  const totalImpressions = monetizedData.reduce((sum, page) => 
    sum + parseInt(page.total_impressions), 0
  );
  
  const avgRPM = totalVideoViews > 0 ? (totalEarnings / totalVideoViews) * 1000 : 0;
  
  const topEarner = monetizedData[0];
  const avgEarningsPerPage = totalEarnings / monetizedData.length;

  return (
    <div className={styles.pageContainer}>
      {/* Key Metrics */}
      <div className={styles.metricsGrid}>
        <MetricCard
          title="Total Earnings"
          value={totalEarnings}
          icon={<FontAwesomeIcon icon="dollar-sign" />}
          color="green"
        />
        <MetricCard
          title="Monetized Pages"
          value={monetizedData.length}
          icon={<FontAwesomeIcon icon="users" />}
          color="blue"
        />
        <MetricCard
          title="Average RPM"
          value={avgRPM}
          icon={<FontAwesomeIcon icon="chart-line" />}
          color="purple"
        />
        <MetricCard
          title="Total Video Views"
          value={totalVideoViews}
          icon={<FontAwesomeIcon icon="play" />}
          color="orange"
        />
        <MetricCard
          title="Top Earner"
          value={topEarner ? parseFloat(topEarner.total_approximate_earnings) : 0}
          icon={<FontAwesomeIcon icon="crown" />}
          color="red"
        />
      </div>

      {/* Monetized Pages List - Full Width */}
      <div className={styles.dashboardCard} style={{ marginBottom: '24px' }}>
        <MonetizedPagesList 
          pages={monetizedData}
          onPageClick={openPageDetails}
          timeRange={timeRange}
        />
      </div>

      {/* Monetization Charts - Half Width */}
      <div className={styles.dashboardCard}>
        <MonetizationCharts 
          data={monetizedData}
          timeRange={timeRange}
        />
      </div>

      {/* Page Details Modal */}
      {selectedPageData && (
        <PageDetailsModal
          pageData={selectedPageData}
          isOpen={isPageDetailsOpen}
          onClose={closePageDetails}
          timeRange={timeRange}
        />
      )}
    </div>
  );
};

export default MonetizedPage; 